import { Damage, Image, Inspection, Sight, VehiclePart, View } from '@monkvision/types';
import { MonkState } from '@monkvision/common';
import { sights } from '@monkvision/sights';
import {
  BEAUTY_SHOT_SIGHT_LABEL,
  PdfDamagedPart,
  PdfInspectionData,
  PdfInteriorDamage,
  PdfPhoto,
  PdfVehicleDetails,
  PdfVehicleSideData,
  SIDE_REFERENCE_SIGHT_LABEL,
  WHEEL_PART_SIGHT_ID,
} from './types';
import {
  CroppedPhotoDamage,
  drawCroppedPhotoImage,
  drawSideReferencePhotoImage,
  IndexedBoundingBox,
  ResizedImage,
  resizeImage,
} from './imageProcessing';
import { Dampart, generateDamparts, PdfVehicleSide, VEHICLE_PARTS_SIDE } from '../../utils';
import { TeslaAdditionalData } from '../useCreateTeslaInspectionLink/types';

interface ApiInteriorDampart {
  area: string;
  damage_type: string;
  repair_cost: number;
}

interface PartImageScore {
  uniqueVisibleDamageTypes: number;
  partSize: number;
}

interface InspectionImages {
  lastTakenImages: ResizedImage[];
  allImages: ResizedImage[];
}

type PartialDamagedPart = Omit<PdfDamagedPart, 'croppedPhotoUrls'>;

async function getInspectionImages(
  inspection: Inspection,
  entities: MonkState,
  sightsOrder: Sight[],
): Promise<InspectionImages> {
  const inspectionImages: InspectionImages = { lastTakenImages: [], allImages: [] };
  const imagesBySightLabel: Record<string, ResizedImage> = {};

  inspectionImages.allImages = await Promise.all(
    entities.images
      .filter((image) => image.inspectionId === inspection.id)
      .map((image) => resizeImage(image)),
  );

  inspectionImages.allImages.forEach((image) => {
    if (image.inspectionId === inspection.id && image.sightId) {
      const sightLabel = sights[image.sightId]?.label;
      if (!sightLabel) {
        return;
      }
      const { createdAt } = image;
      const existingImage = imagesBySightLabel[sightLabel];
      const existingCreatedAt = existingImage?.createdAt;

      if (
        !existingImage ||
        (createdAt && (!existingCreatedAt || new Date(existingCreatedAt) < new Date(createdAt)))
      ) {
        imagesBySightLabel[sightLabel] = image;
      }
    }
  });

  inspectionImages.lastTakenImages = Object.entries(imagesBySightLabel)
    .map(([label, image]) => ({
      image,
      sortIndex: sightsOrder.map((sight) => sight.label).indexOf(label),
    }))
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .map(({ image }) => image);

  return inspectionImages;
}

function getInspectionDate(inspection: Inspection, images: InspectionImages): Date | null {
  let inspectionDate: Date | null = null;
  images.allImages.forEach((image) => {
    if (image.inspectionId === inspection.id && image.additionalData?.created_at) {
      const date = new Date(image.additionalData.created_at);
      inspectionDate = !inspectionDate || inspectionDate < date ? date : inspectionDate;
    }
  });
  return inspectionDate;
}

function getTotalCost(inspection: Inspection, damparts: Dampart[]): number {
  const otherDamages = (inspection.additionalData as TeslaAdditionalData).other_damages;
  const exteriorCost = damparts.reduce((prev, { repairCost }) => prev + repairCost, 0);
  const interiorCost = otherDamages
    ? otherDamages.reduce((prev, damage) => prev + damage.repair_cost, 0)
    : 0;
  return exteriorCost + interiorCost;
}

function generateVehicleDetails(
  inspection: Inspection,
  images: InspectionImages,
  damparts: Dampart[],
  entities: MonkState,
): PdfVehicleDetails {
  const vehicle = entities.vehicles.find((v) => v.inspectionId === inspection.id);
  if (!vehicle) {
    throw new Error('Vehicle is undefined in Tesla PDF.');
  }

  return {
    model: vehicle.model ?? '',
    vin: vehicle.vin ?? '',
    inspectionDate: getInspectionDate(inspection, images),
    mileage: vehicle.mileageValue ?? 0,
    totalCost: getTotalCost(inspection, damparts),
    beautyShotUrl:
      images.lastTakenImages.find(
        (image) =>
          image.additionalData?.sight_id &&
          sights[image.additionalData.sight_id]?.label === BEAUTY_SHOT_SIGHT_LABEL,
      )?.resizedUrl ?? images.lastTakenImages[0].path,
  };
}

function generatePdfPhotos(images: InspectionImages): PdfPhoto[] {
  return images.lastTakenImages.map((image) => ({
    url: image.resizedUrl,
    sight: sights[image.sightId ?? ''],
  }));
}

function mappingApiInteriorDamage(interiorDamages: ApiInteriorDampart[]): PdfInteriorDamage[] {
  return interiorDamages.map((damage) => {
    return { area: damage.area, damageTypes: damage.damage_type, deduction: damage.repair_cost };
  });
}

function generateInteriorDamparts(inspection: Inspection): PdfInteriorDamage[] {
  const interiorDamages = inspection.additionalData?.['other_damages'];
  if (!interiorDamages) {
    return [];
  }
  return mappingApiInteriorDamage(interiorDamages as ApiInteriorDampart[]);
}

function getPartImageScore(name: VehiclePart, image: Image, entities: MonkState): PartImageScore {
  const part = entities.parts.find((p) => p.type === name);
  if (!part) {
    return { uniqueVisibleDamageTypes: 0, partSize: 0 };
  }
  const partDamages = part.damages
    .map((damageId) => entities.damages.find((damage) => damage.id === damageId))
    .filter((damage) => !!damage) as Damage[];
  const imageViews = entities.views.filter((view) => image.views.includes(view.id));
  const visibleDamageTypes = new Set();
  let partSize = 0;
  imageViews.forEach((view) => {
    if (view.elementId === part.id && view.imageRegion.specification.boundingBox) {
      const { width, height } = view.imageRegion.specification.boundingBox;
      partSize = width * height;
    } else {
      const damage = partDamages.find((d) => d.id === view.elementId);
      if (damage) {
        visibleDamageTypes.add(damage.type);
      }
    }
  });
  return {
    uniqueVisibleDamageTypes: visibleDamageTypes.size,
    partSize,
  };
}

async function generateCroppedPhotoUrls(
  images: InspectionImages,
  name: VehiclePart,
  entities: MonkState,
): Promise<string[]> {
  const part = entities.parts.find((p) => p.type === name);
  if (!part) {
    return [];
  }
  const damages = part.damages
    .map((id) => entities.damages.find((damage) => damage.id === id))
    .filter((damage) => !!damage) as Damage[];
  const originalPhotos = images.allImages
    .map((image) => ({ image, score: getPartImageScore(name, image, entities) }))
    .filter(({ score }) => score.uniqueVisibleDamageTypes > 0 && score.partSize > 0)
    .sort((a, b) =>
      a.score.uniqueVisibleDamageTypes === b.score.uniqueVisibleDamageTypes
        ? b.score.partSize - a.score.partSize
        : b.score.uniqueVisibleDamageTypes - a.score.uniqueVisibleDamageTypes,
    )
    .map(({ image }) => image)
    .slice(0, 3);

  const promises = originalPhotos.map((image) => {
    const imageViewsByElementId: Record<string, View> = {};
    image.views.forEach((id) => {
      const view = entities.views.find((v) => v.id === id);
      if (view) {
        imageViewsByElementId[view.elementId] = view;
      }
    });
    const croppedPhotoDamages: CroppedPhotoDamage[] = [];
    damages.forEach((damage) => {
      const damageView = imageViewsByElementId[damage.id];
      if (
        damageView?.imageRegion?.specification?.polygons &&
        damageView.imageRegion.specification.boundingBox
      ) {
        croppedPhotoDamages.push({
          type: damage.type,
          polygons: damageView.imageRegion.specification.polygons,
          boundingBox: damageView.imageRegion.specification.boundingBox,
        });
      }
    });

    return drawCroppedPhotoImage(image, croppedPhotoDamages);
  });
  const croppedPhotoUrls = await Promise.all(promises);
  if (Object.keys(WHEEL_PART_SIGHT_ID).includes(name)) {
    const imageWheel = images.lastTakenImages.find(
      (image) => image.sightId && WHEEL_PART_SIGHT_ID[name]?.includes(image.sightId),
    )?.resizedUrl;
    if (imageWheel) {
      croppedPhotoUrls.push(imageWheel);
    }
  }

  return croppedPhotoUrls.filter((url) => !!url) as string[];
}

async function generateVehicleSideReferencePhotoUrl(
  side: PdfVehicleSide,
  images: InspectionImages,
  damagedParts: PdfDamagedPart[],
  entities: MonkState,
): Promise<string> {
  const originalImage = images.lastTakenImages.find(
    (image) =>
      image.sightId && SIDE_REFERENCE_SIGHT_LABEL[side].includes(sights[image.sightId]?.label),
  );
  if (!originalImage) {
    return '';
  }
  const views = originalImage.views
    .map((id) => entities.views.find((view) => view.id === id))
    .filter((view) => view) as View[];
  const boundingBoxes = damagedParts
    .map(({ id, name }) => {
      const part = entities.parts.find((p) => p.type === name);
      if (!part) {
        return null;
      }
      const view = views.find((v) => v.elementId === part.id);
      if (!view?.imageRegion?.specification.boundingBox) {
        return null;
      }
      return { id, boundingBox: view.imageRegion.specification.boundingBox };
    })
    .filter((bbox) => !!bbox) as IndexedBoundingBox[];

  return drawSideReferencePhotoImage(originalImage, boundingBoxes);
}

async function generateVehicleSideDamagedParts(
  partialDamagedParts: PartialDamagedPart[] | undefined,
  images: InspectionImages,
  entities: MonkState,
): Promise<PdfDamagedPart[] | null> {
  if (!partialDamagedParts) {
    return null;
  }

  const promises = partialDamagedParts.map(
    (partialDamagedPart) =>
      new Promise<PdfDamagedPart>((resolve, reject) => {
        generateCroppedPhotoUrls(images, partialDamagedPart.name, entities)
          .then((croppedPhotoUrls) => {
            resolve({
              ...partialDamagedPart,
              croppedPhotoUrls,
            });
          })
          .catch(reject);
      }),
  );

  return Promise.all(promises);
}

async function createVehicleSideData(
  side: PdfVehicleSide,
  images: InspectionImages,
  damagedParts: PdfDamagedPart[] | null,
  entities: MonkState,
): Promise<PdfVehicleSideData | null> {
  if (!damagedParts) {
    return Promise.resolve(null);
  }
  const referencePhotoUrl = await generateVehicleSideReferencePhotoUrl(
    side,
    images,
    damagedParts,
    entities,
  );

  return {
    side,
    referencePhotoUrl,
    damagedParts,
  };
}

async function generateDamagedVehicleSides(
  images: InspectionImages,
  damparts: Dampart[],
  entities: MonkState,
): Promise<PdfVehicleSideData[]> {
  const partialDamagedParts: Partial<Record<PdfVehicleSide, PartialDamagedPart[]>> = {};
  damparts.forEach((dampart) => {
    const partSide = VEHICLE_PARTS_SIDE[dampart.part];
    partialDamagedParts[partSide] = partialDamagedParts[partSide] ?? [];
    partialDamagedParts[partSide]?.push({
      id: -1,
      name: dampart.part,
      damages: dampart.damageTypes,
      repairCost: dampart.repairCost,
      closeUpPhotoUrls: dampart.closeUpPhotoUrls,
    });
  });
  Object.keys(partialDamagedParts).forEach((key) => {
    const side = key as PdfVehicleSide;
    partialDamagedParts[side] = partialDamagedParts[side]?.sort(
      (a, b) => b.repairCost - a.repairCost,
    );
  });
  let id = 0;
  Object.values(PdfVehicleSide).forEach((side) => {
    partialDamagedParts[side] = partialDamagedParts[side]?.map((partialDamagedPart) => {
      if (!partialDamagedPart) {
        return partialDamagedPart;
      }
      id += 1;
      return {
        ...partialDamagedPart,
        id,
      };
    });
  });

  const promises = Object.values(PdfVehicleSide).map(
    (side) =>
      new Promise<PdfVehicleSideData | null>((resolve, reject) => {
        generateVehicleSideDamagedParts(partialDamagedParts[side], images, entities)
          .then((damagedParts) => createVehicleSideData(side, images, damagedParts, entities))
          .then((res) => resolve(res))
          .catch(reject);
      }),
  );
  const vehicleSides = await Promise.all(promises);
  return vehicleSides.filter((vehicleSide) => !!vehicleSide) as PdfVehicleSideData[];
}

export async function generateTeslaPdfInspectionData(
  inspection: Inspection,
  entities: MonkState,
  sightsOrder: Sight[],
): Promise<PdfInspectionData> {
  const images = await getInspectionImages(inspection, entities, sightsOrder);
  const damparts = generateDamparts(inspection, entities);
  const interiorDamages = generateInteriorDamparts(inspection);
  const vehicle = generateVehicleDetails(inspection, images, damparts, entities);
  const photos = generatePdfPhotos(images);
  const damagedSides = await generateDamagedVehicleSides(images, damparts, entities);

  return {
    inspectionId: inspection.id,
    vehicle,
    photos,
    damagedSides,
    interiorDamages,
  };
}
